import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FileText, Download, Eye, Loader2, AlertTriangle, XCircle, Check, FolderOpen } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function formatFileSize(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function PublicBundle() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageState, setPageState] = useState("loading");
  const [bundle, setBundle]   = useState(null);
  const [activeShareId, setActiveShareId] = useState(null);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [viewing, setViewing]   = useState(null);
  const [dlIds, setDlIds]       = useState(new Set());
  const [bulkDl, setBulkDl]     = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/public/bundles/${token}`)
      .then(async res => {
        if (res.ok) {
          const data = await res.json();
          setBundle(data);
          setPageState("active");
          // Sélectionner le dossier indiqué dans l'URL ou le premier
          const urlShare = searchParams.get("dossier");
          const first = data.shares?.[0]?.id;
          setActiveShareId(urlShare && data.shares?.find(s => s.id === urlShare) ? urlShare : first || null);
        } else if (res.status === 403) {
          const body = await res.json().catch(() => ({}));
          setPageState(body.detail === "expired" ? "expired" : "inactive");
        } else {
          setPageState("expired");
        }
      })
      .catch(() => setPageState("error"));
  }, [token]);

  // Synchroniser l'URL avec le dossier actif
  useEffect(() => {
    if (activeShareId) setSearchParams({ dossier: activeShareId }, { replace: true });
  }, [activeShareId]);

  const activeShare = bundle?.shares?.find(s => s.id === activeShareId) || null;
  const items    = activeShare?.items || [];
  const docItems = items.filter(i => i.item_type === "document");
  const allChecked  = docItems.length > 0 && checkedIds.size === docItems.length;
  const someChecked = checkedIds.size > 0 && !allChecked;

  // Reset sélection quand on change de dossier
  useEffect(() => { setCheckedIds(new Set()); setError(null); }, [activeShareId]);

  const fetchUrl = useCallback(async (shareToken, docId) => {
    const res = await fetch(`${API_BASE}/api/public/shares/${shareToken}/download/${docId}`);
    if (!res.ok) throw res.status;
    return (await res.json()).url;
  }, []);

  const visualise = async (docId) => {
    setViewing(docId); setError(null);
    try { window.open(await fetchUrl(activeShare.token, docId), "_blank", "noopener,noreferrer"); }
    catch (s) { setError(s === 403 ? "Ressource indisponible." : "Ressource introuvable."); }
    finally { setViewing(null); }
  };

  const downloadBlob = async (shareToken, docId, filename) => {
    const url = await fetchUrl(shareToken, docId);
    const resp = await fetch(url);
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = filename || "document.pdf";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const downloadOne = async (docId, filename) => {
    setDlIds(p => new Set([...p, docId])); setError(null);
    try { await downloadBlob(activeShare.token, docId, filename); }
    catch (s) { setError(s === 403 ? "Ressource indisponible." : "Ressource introuvable."); }
    finally { setDlIds(p => { const n = new Set(p); n.delete(docId); return n; }); }
  };

  const downloadSelected = async () => {
    setBulkDl(true); setError(null);
    for (const f of docItems.filter(i => checkedIds.has(i.document_id))) {
      try { await downloadBlob(activeShare.token, f.document_id, f.filename); }
      catch (s) { setError("Erreur lors du téléchargement."); break; }
      await new Promise(r => setTimeout(r, 400));
    }
    setBulkDl(false);
  };

  const toggleAll = () => allChecked ? setCheckedIds(new Set()) : setCheckedIds(new Set(docItems.map(f => f.document_id)));
  const toggleOne = (docId) => setCheckedIds(p => { const n = new Set(p); n.has(docId) ? n.delete(docId) : n.add(docId); return n; });

  // ── Pages d'état ──
  if (pageState === "loading") return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 size={24} strokeWidth={1.5} className="animate-spin text-zinc-400" /></div>
  );
  if (pageState === "inactive") return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="text-center max-w-sm px-6">
        <AlertTriangle size={40} strokeWidth={1} className="text-amber-400 mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-zinc-800 mb-2">Bundle temporairement indisponible</h1>
        <p className="text-sm text-zinc-500">Ce bundle a été désactivé par son propriétaire.</p>
      </div>
    </div>
  );
  if (pageState !== "active") return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="text-center max-w-sm px-6">
        <XCircle size={40} strokeWidth={1} className="text-zinc-300 mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-zinc-800 mb-2">Ressource introuvable</h1>
        <p className="text-sm text-zinc-500">Ce lien est expiré ou n'existe pas.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 px-6 py-4 shrink-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono mb-0.5">BeDocs · Bundle</div>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-zinc-900">{bundle?.title}</h1>
          {bundle?.expiration_date && (
            <span className="text-xs font-mono text-zinc-400">Valide jusqu'au {new Date(bundle.expiration_date).toLocaleDateString("fr-FR")}</span>
          )}
        </div>
      </header>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar navigation dossiers */}
        <nav className="w-56 shrink-0 bg-white border-r border-zinc-200 overflow-y-auto">
          <div className="px-3 py-2.5 border-b border-zinc-100">
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">Dossiers · {bundle?.shares?.length}</span>
          </div>
          {(bundle?.shares || []).map(s => (
            <button key={s.id} onClick={() => setActiveShareId(s.id)} data-testid={`bundle-nav-${s.id}`}
              className={`w-full text-left px-3 py-3 border-b border-zinc-100 transition-colors flex items-center gap-2 ${
                s.id === activeShareId ? "bg-[#002FA7]/5 border-l-2 border-l-[#002FA7]" : "hover:bg-zinc-50"
              }`}>
              <FolderOpen size={14} strokeWidth={1.5} className={s.id === activeShareId ? "text-[#002FA7]" : "text-zinc-400"} />
              <span className={`text-sm truncate ${s.id === activeShareId ? "font-medium text-[#002FA7]" : "text-zinc-700"}`}>{s.title}</span>
            </button>
          ))}
        </nav>

        {/* Contenu du dossier sélectionné */}
        <main className="flex-1 overflow-y-auto px-6 py-6">
          {!activeShare ? (
            <div className="text-center py-12 text-sm text-zinc-400">Sélectionnez un dossier.</div>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-zinc-800 mb-4">{activeShare.title}</h2>

              {error && (
                <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                  <AlertTriangle size={14} strokeWidth={1.5} className="shrink-0" />{error}
                  <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-700"><XCircle size={14} /></button>
                </div>
              )}

              {checkedIds.size > 0 && (
                <div data-testid="bulk-download-bar" className="mb-3 flex items-center justify-between bg-[#002FA7]/5 border border-[#002FA7]/20 px-4 py-2.5">
                  <span className="text-sm font-mono text-zinc-700">{checkedIds.size} fichier{checkedIds.size > 1 ? "s" : ""} sélectionné{checkedIds.size > 1 ? "s" : ""}</span>
                  <button data-testid="download-selected-btn" onClick={downloadSelected} disabled={bulkDl}
                    className="inline-flex items-center gap-2 text-sm font-medium text-white bg-[#002FA7] hover:bg-[#00227A] px-4 py-2 transition-colors disabled:opacity-50">
                    {bulkDl ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                    {bulkDl ? "Téléchargement…" : `Télécharger (${checkedIds.size})`}
                  </button>
                </div>
              )}

              {items.length === 0 ? (
                <div className="text-center py-8 text-sm text-zinc-400">Aucun fichier dans ce dossier.</div>
              ) : (
                <div className="bg-white border border-zinc-200 overflow-hidden">
                  <table className="w-full text-sm" data-testid="bundle-files-table">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-mono text-zinc-500">
                        <th className="w-10 px-4 py-2.5 text-center">
                          {docItems.length > 0 && (
                            <button onClick={toggleAll} data-testid="select-all-checkbox"
                              className={`w-4 h-4 border flex items-center justify-center mx-auto transition-colors ${allChecked ? "bg-[#002FA7] border-[#002FA7]" : someChecked ? "bg-[#002FA7]/20 border-[#002FA7]" : "border-zinc-300 hover:border-zinc-500"}`}>
                              {allChecked && <Check size={10} strokeWidth={2.5} className="text-white" />}
                              {someChecked && <span className="w-2 h-0.5 bg-[#002FA7]" />}
                            </button>
                          )}
                        </th>
                        <th className="px-4 py-2.5 text-left">Nom du document</th>
                        <th className="px-4 py-2.5 text-right whitespace-nowrap">Pages</th>
                        <th className="px-4 py-2.5 text-right whitespace-nowrap">Taille</th>
                        <th className="px-4 py-2.5 text-center" colSpan={2}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {items.map(item => {
                        if (item.item_type === "section") return (
                          <tr key={item.id} className="bg-zinc-50">
                            <td colSpan={6} className="px-4 py-3">
                              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">{item.content || "Section"}</span>
                            </td>
                          </tr>
                        );
                        if (item.item_type === "comment") return (
                          <tr key={item.id}>
                            <td colSpan={6} className="pl-10 pr-4 py-2">
                              <span className="text-sm text-zinc-500">{item.content}</span>
                            </td>
                          </tr>
                        );
                        // Document
                        const docId = item.document_id;
                        const isViewing_ = viewing === docId;
                        const isDl = dlIds.has(docId);
                        const isChecked = checkedIds.has(docId);
                        return (
                          <tr key={item.id} data-testid={`bundle-file-${item.id}`}
                            className={`transition-colors ${isChecked ? "bg-[#e6f1ff]" : "hover:bg-zinc-50"}`}>
                            <td className="px-4 py-3 text-center">
                              <button onClick={() => toggleOne(docId)}
                                className={`w-4 h-4 border flex items-center justify-center mx-auto transition-colors ${isChecked ? "bg-[#002FA7] border-[#002FA7]" : "border-zinc-300 hover:border-zinc-500"}`}>
                                {isChecked && <Check size={10} strokeWidth={2.5} className="text-white" />}
                              </button>
                            </td>
                            <td className="py-3">
                              <div className="flex items-center gap-2 min-w-0 pl-10">
                                <FileText size={14} strokeWidth={1.5} className="text-[#DC2626] shrink-0" />
                                <span className="truncate text-sm">{item.filename}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-zinc-500 whitespace-nowrap">{item.page_count ?? "—"}</td>
                            <td className="px-4 py-3 text-right font-mono text-zinc-500 whitespace-nowrap">{formatFileSize(item.size_bytes)}</td>
                            <td className="px-3 py-3 text-center">
                              <button onClick={() => visualise(docId)} disabled={isViewing_ || isDl}
                                className="inline-flex items-center gap-1.5 text-xs font-medium border border-zinc-300 text-zinc-600 hover:border-[#002FA7] hover:text-[#002FA7] px-3 py-1.5 transition-colors disabled:opacity-40 whitespace-nowrap">
                                {isViewing_ ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Visualiser
                              </button>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <button onClick={() => downloadOne(docId, item.filename)} disabled={isDl || isViewing_}
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#002FA7] hover:bg-[#00227A] px-3 py-1.5 transition-colors disabled:opacity-40 whitespace-nowrap">
                                {isDl ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Télécharger
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="mt-3 text-xs font-mono text-zinc-400 text-right">
                {docItems.length} document{docItems.length > 1 ? "s" : ""}
                {items.length > docItems.length ? ` · ${items.length - docItems.length} section(s)/commentaire(s)` : ""}
              </p>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
